// client/components/ui/ErrorBoundary.jsx
// ✅ Enterprise-Level Error Boundary mit Retry & Reporting
import React from "react";
import { AlertTriangle, RefreshCw, Home, ChevronDown } from "lucide-react";
import ThemeContext from "../../context/ThemeContext.jsx";
import AppButton from "./AppButton.jsx";
import { cn } from "../../utils/themeClasses.js";
import { clearChunkReloadFlags, tryRecoverChunkError } from "../../utils/lazyWithRetry.js";

/**
 * Error Boundary Komponente
 * Fängt JavaScript-Fehler in Child-Komponenten und zeigt Fallback-UI
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 * @param {React.ReactNode} props.fallback - Optional custom fallback UI
 * @param {string} props.name - Name der Boundary für Logging
 * @param {Function} props.onError - Callback bei Fehler
 * @param {boolean} props.resetOnNavigation - Reset bei Route-Änderung
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0
    };
  }
  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error
    };
  }
  componentDidCatch(error, errorInfo) {
    const {
      name = "Unknown",
      onError
    } = this.props;

    // Veraltete Lazy-Chunks nach Client-Rebuild → Hard-Reload (einmal pro Zyklus)
    if (tryRecoverChunkError(error)) {
      return;
    }

    // Fehler loggen
    console.error(`❌ ErrorBoundary [${name}]:`, error);
    console.error("Component Stack:", errorInfo?.componentStack);

    // State mit Error-Info aktualisieren
    this.setState({
      errorInfo
    });

    // Optional: Error-Callback aufrufen (für zentrales Logging)
    if (typeof onError === "function") {
      onError(error, errorInfo, name);
    }

    // Optional: An Backend-Endpoint senden (für Monitoring)
    this.reportError(error, errorInfo, name);
  }
  reportError = async (error, errorInfo, boundaryName) => {
    // In Development nicht senden
    if (import.meta.env.DEV) return;
    try {
      await fetch("/api/client-error", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          message: error?.message || "Unknown error",
          stack: error?.stack,
          componentStack: errorInfo?.componentStack,
          boundary: boundaryName,
          url: window.location.href,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString()
        })
      });
    } catch (e) {
      // Silently fail - wir wollen keine Fehler-Loops
    }
  };
  handleReset = () => {
    clearChunkReloadFlags();
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: this.state.retryCount + 1
    });
  };
  handleReload = () => {
    clearChunkReloadFlags();
    window.location.reload();
  };
  handleGoHome = () => {
    window.location.href = "/";
  };
  render() {
    const {
      hasError,
      error,
      errorInfo,
      retryCount
    } = this.state;
    const {
      children,
      fallback,
      name = "Component"
    } = this.props;
    if (hasError) {
      // Custom Fallback wenn vorhanden
      if (fallback) {
        return typeof fallback === "function" ? fallback({
          error,
          reset: this.handleReset
        }) : fallback;
      }

      // Standard Fallback UI
      return <ThemeContext.Consumer>
          {ctx => {
          const isDark = ctx?.isDark !== false;
          return <div className="min-h-[200px] flex items-center justify-center p-4">
          <div className="max-w-lg w-full glass rounded-2xl border border-red-500/20 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-red-400">Fehler aufgetreten</h2>
                <p className={`text-sm ${"text-blue-200/60"}`}>
                  {name} konnte nicht geladen werden
                </p>
              </div>
            </div>

            <p className={`mb-6 ${"text-blue-200/80"}`}>
              Es ist ein unerwarteter Fehler aufgetreten. 
              Versuche es erneut oder lade die Seite neu.
            </p>

            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
              {retryCount < 3 && (
                <AppButton
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={this.handleReset}
                  className="inline-flex items-center justify-center gap-2 w-full sm:w-auto font-semibold"
                >
                  <RefreshCw className="w-3.5 h-3.5 shrink-0" />
                  Erneut versuchen
                  {retryCount > 0 && (
                    <span className={`text-[11px] font-normal ${"text-white/70"}`}>({3 - retryCount} verbleibend)</span>
                  )}
                </AppButton>
              )}
              <AppButton
                type="button"
                variant="secondary"
                size="sm"
                onClick={this.handleReload}
                className={cn(
                  "inline-flex items-center justify-center gap-2 w-full sm:w-auto font-normal",
                  "!bg-zinc-700/30 !text-zinc-300 !border-zinc-600/20 hover:!bg-zinc-700/50",
                )}
              >
                <RefreshCw className="w-3.5 h-3.5 shrink-0" />
                Seite neu laden
              </AppButton>
              <AppButton
                type="button"
                variant="ghost"
                size="sm"
                onClick={this.handleGoHome}
                className="inline-flex items-center justify-center gap-2 w-full sm:w-auto font-normal seg-btn-idle"
              >
                <Home className="w-3.5 h-3.5 shrink-0" />
                Zur Startseite
              </AppButton>
            </div>

            {import.meta.env.DEV && error && <details className="mt-6 group">
                <summary className={`cursor-pointer text-sm flex items-center gap-2 ${"text-blue-300/60"}`}>
                  <ChevronDown className="w-4 h-4 transition group-open:rotate-180" />
                  Fehlerdetails (Entwicklung)
                </summary>
                <div className="mt-3 space-y-3">
                  <div className="bg-red-900/20 border border-red-500/20 rounded-lg p-3">
                    <p className="text-xs font-mono text-red-300 break-all">{error.toString()}</p>
                  </div>
                  {error.stack && <div className={"bg-black/30 rounded-lg p-3"}>
                      <p className={`text-xs mb-1 ${"text-blue-300/60"}`}>Stack Trace:</p>
                      <pre className={`text-xs font-mono overflow-auto max-h-40 whitespace-pre-wrap ${"text-blue-200/70"}`}>
                        {error.stack}
                      </pre>
                    </div>}
                  {errorInfo?.componentStack && <div className={"bg-black/30 rounded-lg p-3"}>
                      <p className={`text-xs mb-1 ${"text-blue-300/60"}`}>Component Stack:</p>
                      <pre className={`text-xs font-mono overflow-auto max-h-40 whitespace-pre-wrap ${"text-blue-200/70"}`}>
                        {errorInfo.componentStack}
                      </pre>
                    </div>}
                </div>
              </details>}
          </div>
        </div>;
        }}
        </ThemeContext.Consumer>;
    }
    return children;
  }
}

/**
 * Kleinere Error Boundary für einzelne Blöcke
 * Zeigt inline Fehler ohne die ganze Seite zu blockieren (theme-aware)
 */
export function BlockErrorBoundary({
  children,
  name = "Block"
}) {
  return <ErrorBoundary name={name} fallback={({
    error,
    reset
  }) => <ThemeContext.Consumer>
          {ctx => {
      const isDark = ctx?.isDark !== false;
      return <div className={`p-4 border rounded-xl ${"bg-red-500/10 border-red-500/20"}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                    <span className={`text-sm ${"text-red-300"}`}>
                      {name} konnte nicht geladen werden
                    </span>
                  </div>
                  <AppButton type="button" variant="ghost" size="sm" onClick={reset} className={cn("text-xs px-2.5 py-1.5 min-h-0 h-8 font-normal", "!bg-red-500/20 !text-red-300 hover:!bg-red-500/30")}>
                    Erneut versuchen
                  </AppButton>
                </div>
              </div>;
    }}
        </ThemeContext.Consumer>}>
      {children}
    </ErrorBoundary>;
}

/**
 * HOC für Error Boundary
 * @param {React.Component} Component 
 * @param {string} name 
 */
export function withErrorBoundary(Component, name) {
  const displayName = name || Component.displayName || Component.name || "Component";
  function WrappedComponent(props) {
    return <ErrorBoundary name={displayName}>
        <Component {...props} />
      </ErrorBoundary>;
  }
  WrappedComponent.displayName = `WithErrorBoundary(${displayName})`;
  return WrappedComponent;
}
export default ErrorBoundary;